import React, { useState, useEffect } from 'react'
import { debounce } from 'throttle-debounce'
import { ExclamationCircleIcon } from '@heroicons/react/solid'

import { useForm } from 'react-hook-form'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import PrimaryButton from '@/shared/Buttons/Primary'

import Card from '@/shared/Card'

const UsernameSettings = () => {
  const { putpostRequest } = useQuery()
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const [loading, setLoading] = useState(false)
  const [customError, setCustomError] = useState(null)
  const { formState, register, reset, handleSubmit, setError, watch } = useForm({
    defaultValues: {
      username: '',
      display_name: ''
    }
  })

  // Populate form when currentUser is available
  useEffect(() => {
    if (currentUser) {
      reset({
        username: currentUser.username || '',
        display_name: currentUser.displayName || ''
      })
    }
  }, [currentUser, reset])

  const { isDirty, errors } = formState
  const usernameValue = watch('username')

  const keyPress = (e) => {
    if (e.target.name === 'name' && e.keyCode === 13) { e.preventDefault() }
    if ((e.metaKey || e.ctrlKey) && e.keyCode === 13) {
      if (typeof (e.target.name) === 'undefined') {
        e.target.blur() // froala hack to make it update model so it can save properly
      }
      handleSubmit(onSubmit)()
    }
  }

  const onSubmit = debounce(300, (data) => {
    setLoading(true)
    setCustomError(null)

    const payload = {
      user: {
        username: data.username?.toLowerCase().trim(),
        display_name: data.display_name?.trim()
      }
    }

    putpostRequest('/api/v3/users/update_username', 'PATCH', payload, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        if (typeof err !== 'string') { // 500 code on API
          Object.entries(err).forEach(([key, value]) => {
            setError(key, { message: Array.isArray(value) ? value.join(', ') : value })
          })
        }
        if (typeof err === 'string') { setCustomError(err) }
        return
      }

      // Update the global user state
      setCurrentUser({
        ...currentUser,
        username: jsonData.user.username,
        displayName: jsonData.user.display_name
      })
      
      reset({
        username: jsonData.user.username,
        display_name: jsonData.user.display_name
      })
      
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-green-300 text-green-600">Username & Display Name Updated</p>
        <p className="mt-1 text-sm dark:text-green-300 text-green-400">Your profile information has been successfully updated.</p>
      </div>)
    })
  })

  return <>
    <div className="md:col-span-1">
      <div className="px-0 sm:px-4 sm:px-0 mb-3">
        <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">Username & Display Name</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Your username is used in your profile URL and for mentions. Your display name is what others see.
        </p>
      </div>
    </div>
    <Card title='Username & Display Name' footer={
      <div className='flex flex-rows justify-end items-end'>
        <div className='flex flex-col items-end w-min ml-2'>
          <div className='flex flex-col items-end w-min ml-2'>
            <PrimaryButton className='w-full' onClick={handleSubmit(onSubmit)} disabled={!isDirty} loading={loading} text={<div className='flex flex-col'>
              Save
              { isDirty && <div className="hidden sm:block w-min text-xs px-2 font-sans text-gray-50">⌘/ctrl+ENTER</div> }
            </div>} />
          </div>
        </div>
      </div>
      } >
        <form>
          <div className="grid sm:grid-cols-4 gap-6">
            <div className="col-span-4 sm:col-span-2">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">Username</label>
              <div className="mt-1 flex flex-col rounded-md shadow-sm">
                <div className='relative'>
                  <input 
                    onKeyDown={keyPress} 
                    type="text" 
                    className={errors.username ? 'errors' : ''} 
                    {...register('username', { 
                      required: "Can't be blank",
                      pattern: {
                        value: /^(?=.*[a-z])[a-z0-9\-_]+$/i,
                        message: 'Numbers 0-9, Letters a-z, hyphens -, or underlines _ only'
                      }
                    })} 
                    placeholder="casting-call-club" 
                  />
                  { errors.username && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div> }
                </div>
              </div>
              <p className="text-xs text-gray-800 dark:text-gray-100">Only numbers, letters, hyphens -, and underlines _</p>
              <p className="text-xs text-gray-800 dark:text-gray-100">It will be the link to your profile: castingcall.club/{usernameValue || 'your-username'}</p>
              { errors.username && <div className='mt-2 text-sm text-red-600'>{errors.username.message}</div> }
            </div>
            <div className="col-span-4" />
            <div className="col-span-4 sm:col-span-2">
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">Display Name</label>
              <div className="mt-1 flex flex-col rounded-md shadow-sm">
                <div className='relative'>
                  <input 
                    onKeyDown={keyPress} 
                    type="text" 
                    className={errors.display_name ? 'errors' : ''} 
                    {...register('display_name', { 
                      required: "We have to call you something. What should we call you?",
                      maxLength: {
                        value: 50,
                        message: "Your display name is too long. Please tone it down a bit."
                      }
                    })} 
                    placeholder="Casting Call Club" 
                  />
                  { errors.display_name && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div> }
                </div>
              </div>
              <p className="text-xs text-gray-800 dark:text-gray-100">This will be publicly displayed.</p>
              { errors.display_name && <div className='mt-2 text-sm text-red-600'>{errors.display_name.message}</div> }
              { customError && <div className='mt-2 text-sm text-red-600'>{customError}</div> }
            </div>
          </div>
        </form>
    </Card>
  </>
}

export default UsernameSettings